import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { listQuery } from './query'
import type { SiteRow } from './sites'

const api = clientFor('/nos/api')

/** 路径状态：active 是仿真的「现网」，disabled 只停用、保留历史记录 */
export type LaneStatus = 'active' | 'disabled'

/** 运输方式：与后端 internal/lane/model.go 的 Modes 同源两份 */
export type LaneMode = 'truck' | 'rail' | 'ship' | 'air'

/** 一条路径：某网点到某网点的直达运输通道，仿真的边权取自 distance_km。 */
export interface Lane {
  id: number
  tenant_id: number
  code: string
  name: string
  from_site_id: number
  to_site_id: number
  distance_km: number
  duration_min: number
  cost_per_km: number
  mode: LaneMode
  status: LaneStatus
  created_at: string
  updated_at: string
}

/** 新建 / 编辑路径的入参：两端网点后端必填（0 与自环都是 422），里程必须大于 0 */
export interface LaneInput {
  code: string
  mode: LaneMode
  status: LaneStatus
  from_site_id?: number
  to_site_id?: number
  name?: string
  distance_km?: number
  duration_min?: number
  cost_per_km?: number
}

export interface LaneQuery extends PageQuery {
  keyword?: string
  status?: string
  mode?: string
  from_site_id?: number
  to_site_id?: number
}

/** 表单态：空文本用 ''，没选的网点 id 用 undefined */
export interface LaneForm {
  code: string
  name: string
  from_site_id: number | undefined
  to_site_id: number | undefined
  distance_km: number | undefined
  duration_min: number | undefined
  cost_per_km: number | undefined
  mode: LaneMode
  status: LaneStatus
}

/** 运输方式字典，顺序与后端 lane.Modes 一致，即前端下拉的展示顺序 */
export const MODES: LaneMode[] = ['truck', 'rail', 'ship', 'air']

/** 与后端 services/nos/internal/lane/model.go 的 ModeNames 同源两份 */
export const MODE_LABELS: Record<LaneMode, string> = {
  truck: '公路',
  rail: '铁路',
  ship: '水运',
  air: '航空',
}

/** 与后端 services/nos/internal/lane/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<LaneStatus, string> = {
  active: '启用',
  disabled: '停用',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<LaneStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  active: 'success',
  disabled: 'info',
}

/**
 * 表单校验：抄后端 internal/lane/service.go 的 validate，
 * 外加自环判断（后端 422「起运网点与目的网点不能相同」，表单先行一步少一次往返）。
 */
export function laneFormError(form: LaneForm): string {
  const code = form.code.trim()
  if (!code) return '请输入路径编码'
  if ([...code].length > 32) return '路径编码不能超过 32 个字符'
  if ([...form.name.trim()].length > 64) return '路径名称不能超过 64 个字符'
  if (!form.from_site_id) return '请选择起运网点'
  if (!form.to_site_id) return '请选择目的网点'
  if (form.from_site_id === form.to_site_id) return '起运网点与目的网点不能相同'
  if (form.distance_km === undefined || !(form.distance_km > 0)) return '路径里程必须大于 0'
  if (form.duration_min !== undefined && form.duration_min < 0) return '路径时长不能为负数'
  if (form.cost_per_km !== undefined && form.cost_per_km < 0) return '单公里成本不能为负数'
  return ''
}

/** 表单 → 请求体：没选的网点 id 与没填的数值不进 body（undefined 会被 JSON.stringify 丢掉） */
export function laneInput(form: LaneForm): LaneInput {
  const input: LaneInput = {
    code: form.code.trim(),
    mode: form.mode,
    status: form.status,
  }
  const name = form.name.trim()
  if (name) input.name = name
  if (form.from_site_id) input.from_site_id = form.from_site_id
  if (form.to_site_id) input.to_site_id = form.to_site_id
  if (form.distance_km !== undefined && !Number.isNaN(form.distance_km)) {
    input.distance_km = Number(form.distance_km)
  }
  if (form.duration_min !== undefined && !Number.isNaN(form.duration_min)) {
    input.duration_min = Number(form.duration_min)
  }
  if (form.cost_per_km !== undefined && !Number.isNaN(form.cost_per_km)) {
    input.cost_per_km = Number(form.cost_per_km)
  }
  return input
}

export type LaneRow = Lane

export const listLanes = (q: LaneQuery): Promise<PageResult<LaneRow>> =>
  api.get<PageResult<LaneRow>>('/lanes', listQuery({ ...q }))

export const getLane = (id: number): Promise<LaneRow> => api.get<LaneRow>(`/lanes/${id}`)

export const createLane = (input: LaneInput): Promise<LaneRow> => api.post<LaneRow>('/lanes', input)

export const updateLane = (id: number, input: LaneInput): Promise<LaneRow> =>
  api.put<LaneRow>(`/lanes/${id}`, input)

export const deleteLane = (id: number): Promise<void> => api.del<void>(`/lanes/${id}`)

/** 下拉参照用的全量路径：后端 size 上限 200 */
export const listLaneOptions = (): Promise<LaneRow[]> =>
  listLanes({ page: 1, size: 200 }).then((page) => page.list)

/**
 * 网点下拉的展示文案：`编码 名称`。列表里没有网点名称（只给 id），
 * 所以要拿参照列表拼出来；参照没加载到就退回「#id」，不能因此把整列渲染成空白。
 */
export function siteLabel(sites: SiteRow[], id: number | undefined | null): string {
  if (!id) return '—'
  const hit = sites.find((s) => s.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}
